<template>
  <head>
    <meta charset="utf-8" />
    <title>Desk Birdy | Parking Quick Stats</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Rental management system by Nouveta LTD" name="description" />
    <meta content="Kelvin Njuguna" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">

  <!-- Begin page -->
  <div id="layout-wrapper">

    <NavigationBar/>

    <div id="content" class="content-minimize">

      <div class="page-content">
        <div class="container-fluid">

          <ToolBar/>

          <!-- Enhanced Date Range Selection Card -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card border-primary">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-3">
                      <div class="d-flex align-items-center">
                        <i class="mdi mdi-calendar-month text-primary font-24 me-2"></i>
                        <div>
                          <label class="form-label mb-0 fw-semibold">Report Period</label>
                          <p class="text-muted mb-0 small">Select month to view compliance</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label fw-semibold">Select Month</label>
                      <select v-model="selectedMonth" @change="getSaccos()" class="form-select">
                        <option value="">All Time (Current Status)</option>
                        <option v-for="(month, index) in availableMonths" :key="index" :value="month.value">
                          {{ month.label }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label fw-semibold">Select Year</label>
                      <select v-model="selectedYear" @change="getSaccos()" class="form-select">
                        <option v-for="year in availableYears" :key="year" :value="year">
                          {{ year }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label fw-semibold opacity-0">Action</label>
                      <button @click="resetFilters()" class="btn btn-outline-secondary w-100">
                        <i class="mdi mdi-refresh me-2"></i>Reset Filters
                      </button>
                    </div>
                  </div>

                  <!-- Period Info Display -->
                  <div v-if="selectedMonth" class="alert alert-info mt-3 mb-0">
                    <i class="mdi mdi-information-outline me-2"></i>
                    <strong>Showing compliance data for: {{ getSelectedPeriodLabel() }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI Insights Card -->
          <div v-if="aiInsights.generated" class="row mb-3">
            <div class="col-12">
              <div class="card border-success">
                <div class="card-header bg-success text-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0 text-white">
                      <i class="mdi mdi-lightbulb-on me-2"></i>AI-Powered Insights
                    </h5>
                    <button @click="generateInsights()" class="btn btn-sm btn-light">
                      <i class="mdi mdi-refresh"></i> Refresh Insights
                    </button>
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <!-- Best Performers -->
                    <div class="col-md-4">
                      <div class="insight-box border-success">
                        <div class="d-flex align-items-center mb-3">
                          <div class="insight-icon bg-success">
                            <i class="mdi mdi-trophy"></i>
                          </div>
                          <h6 class="mb-0 ms-3">Top Performers</h6>
                        </div>
                        <div v-for="(sacco, index) in aiInsights.bestPerformers" :key="index" class="mb-2">
                          <div class="d-flex justify-content-between align-items-center">
                            <span class="fw-semibold">{{ index + 1 }}. {{ sacco.saccoName }}</span>
                            <span class="badge bg-success">{{ sacco.complianceRate }}%</span>
                          </div>
                          <small class="text-muted">{{ sacco.compliant }}/{{ sacco.total }} vehicles compliant</small>
                        </div>
                      </div>
                    </div>

                    <!-- Needs Attention -->
                    <div class="col-md-4">
                      <div class="insight-box border-danger">
                        <div class="d-flex align-items-center mb-3">
                          <div class="insight-icon bg-danger">
                            <i class="mdi mdi-alert"></i>
                          </div>
                          <h6 class="mb-0 ms-3">Needs Attention</h6>
                        </div>
                        <div v-for="(sacco, index) in aiInsights.worstPerformers" :key="index" class="mb-2">
                          <div class="d-flex justify-content-between align-items-center">
                            <span class="fw-semibold">{{ index + 1 }}. {{ sacco.saccoName }}</span>
                            <span class="badge bg-danger">{{ sacco.complianceRate }}%</span>
                          </div>
                          <small class="text-muted">{{ sacco.compliant }}/{{ sacco.total }} vehicles compliant</small>
                        </div>
                      </div>
                    </div>

                    <!-- Most Improved -->
                    <div class="col-md-4">
                      <div class="insight-box border-warning">
                        <div class="d-flex align-items-center mb-3">
                          <div class="insight-icon bg-warning">
                            <i class="mdi mdi-trending-up"></i>
                          </div>
                          <h6 class="mb-0 ms-3">Most Improved</h6>
                        </div>
                        <div v-for="(sacco, index) in aiInsights.mostImproved" :key="index" class="mb-2">
                          <div class="d-flex justify-content-between align-items-center">
                            <span class="fw-semibold">{{ index + 1 }}. {{ sacco.saccoName }}</span>
                            <span class="badge bg-warning">+{{ sacco.improvement }}%</span>
                          </div>
                          <small class="text-muted">From {{ sacco.previousRate }}% to {{ sacco.currentRate }}%</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Key Metrics & Recommendations -->
                  <div class="row mt-4">
                    <div class="col-md-6">
                      <div class="p-3 bg-light rounded">
                        <h6 class="text-primary mb-3"><i class="mdi mdi-chart-line me-2"></i>Key Metrics</h6>
                        <ul class="list-unstyled mb-0">
                          <li v-for="(metric, index) in aiInsights.keyMetrics" :key="index" class="mb-2">
                            <i class="mdi mdi-check-circle text-success me-2"></i>{{ metric }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="p-3 bg-light rounded">
                        <h6 class="text-primary mb-3"><i class="mdi mdi-lightbulb me-2"></i>Recommendations</h6>
                        <ul class="list-unstyled mb-0">
                          <li v-for="(rec, index) in aiInsights.recommendations" :key="index" class="mb-2">
                            <i class="mdi mdi-arrow-right-circle text-info me-2"></i>{{ rec }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics Cards -->
          <div class="row">
            <div class="col-lg-12 px-sm-30px">
              <div class="card">
                <div class="card-body">
                  <div class="row">
                    <div class="col-sm-12 align-self-center text-capitalize">
                      <div class="text-lg-left mt-4 mt-lg-0">
                        <div class="row">
                          <div class="col-sm-12 col-md-3 col-lg-3 mb-5 mb-md-0">
                            <div>
                              <div class="avatar-sm mb-3">
                                                                <span class="avatar-title rounded-circle bg-warning font-size-20">
                                                                    <i class="mdi mdi-car-multiple text-white"></i>
                                                                </span>
                              </div>
                              <p class="text-muted text-truncate mb-2">Saccos's/Sub Groups</p>
                              <h5 class="mb-0">{{saccos.length}}</h5>
                            </div>
                          </div>

                          <div class="col-sm-6 col-md-3 col-lg-3 mb-5 mb-md-0">
                            <div>
                              <div class="avatar-sm mb-3">
                                                                <span class="avatar-title rounded-circle bg-info font-size-20">
                                                                    <i class="mdi-file-document-edit-outline mdi text-white"></i>
                                                                </span>
                              </div>
                              <p class="text-muted text-truncate mb-2 text-capitalize">
                                {{ selectedMonth ? 'Registered in Period' : 'Registered Vehicles' }}</p>
                              <h5 class="mb-0">{{registeredVehicles}}</h5>
                            </div>
                          </div>

                          <div class="col-sm-6 col-md-3 col-lg-3 mb-5 mb-md-0">
                            <div>
                              <div class="avatar-sm mb-3">
                                                                <span class="avatar-title rounded-circle bg-success font-size-20">
                                                                    <i class="bx bxs-ship text-white"></i>
                                                                </span>
                              </div>
                              <p class="text-muted text-truncate mb-2">Compliant Vehicles</p>
                              <h5 class="mb-0">{{compliantVehicles}}</h5>
                            </div>
                          </div>

                          <div class="col-sm-12 col-md-3 col-lg-3 mb-5 mb-md-0">
                            <div>
                              <div class="avatar-sm mb-3">
                                                                <span class="avatar-title rounded-circle bg-danger font-size-20">
                                                                    <i class="mdi mdi-car-off text-white"></i>
                                                                </span>
                              </div>
                              <p class="text-muted text-truncate mb-2">Uncompliant Vehicles</p>
                              <h5 class="mb-0">{{uncompliantVehicles}}</h5>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Data Table -->
            <div class="col-12 px-sm-30px">
              <div class="card">
                <div class="card-header bg-white border-bottom d-flex justify-content-between">
                  <div>
                    <h4 class="card-title">Registered Sacco's/Sub Groups</h4>
                    <p v-if="selectedMonth" class="text-muted mb-0 small">
                      <i class="mdi mdi-information-outline"></i>
                      Compliance status for {{ getSelectedPeriodLabel() }}
                    </p>
                  </div>
                  <div>
                    <a v-if="getRight('CREATE SACCO')" @click="goTo('parking-fleet-new')" href="javascript: void(0);" type="button" class="btn btn-primary waves-effect waves-light">
                      <i class="bx bx-plus font-size-16 align-middle me-2"></i> Add Sacco/Sub Group
                    </a>

                    <!-- Enhanced Download Dropdown -->
                    <div class="btn-group ms-3" role="group">
                      <button type="button" class="btn btn-success dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <i class="bx bxs-download font-size-16 me-2"></i>
                        {{ downloadStatus.downloading ? downloadStatus.message : 'Download Report' }}
                      </button>
                      <ul class="dropdown-menu">
                        <li><a class="dropdown-item" href="javascript:void(0)" @click="downloadCurrentPage()">
                          <i class="mdi mdi-file-document me-2"></i>Current Page Only
                        </a></li>
                        <li><a class="dropdown-item" href="javascript:void(0)" @click="downloadAllData()">
                          <i class="mdi mdi-file-multiple me-2"></i>All Data (Batch Download)
                        </a></li>
                        <li><hr class="dropdown-divider"></li>
                        <li><a class="dropdown-item" href="javascript:void(0)" @click="downloadWithInsights()">
                          <i class="mdi mdi-brain me-2"></i>AI-Enhanced Report
                        </a></li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Download Progress Modal -->
                <div v-if="downloadStatus.downloading" class="position-fixed top-50 start-50 translate-middle" style="z-index: 9999;">
                  <div class="card shadow-lg" style="min-width: 400px;">
                    <div class="card-body">
                      <h5 class="card-title mb-3">
                        <i class="mdi mdi-download mdi-spin me-2"></i>
                        Downloading Data...
                      </h5>

                      <div class="mb-3">
                        <div class="d-flex justify-content-between mb-2">
                          <span>Progress: {{ downloadStatus.currentBatch }} / {{ downloadStatus.totalBatches }} batches</span>
                          <span>{{ downloadStatus.percentage }}%</span>
                        </div>
                        <div class="progress" style="height: 25px;">
                          <div class="progress-bar progress-bar-striped progress-bar-animated"
                               :style="{width: downloadStatus.percentage + '%'}"
                               role="progressbar">
                            {{ downloadStatus.percentage }}%
                          </div>
                        </div>
                      </div>

                      <div class="row text-center">
                        <div class="col-6">
                          <div class="mb-2">
                            <i class="mdi mdi-database me-1"></i>
                            <strong>{{ downloadStatus.recordsProcessed }}</strong>
                          </div>
                          <small class="text-muted">Records Downloaded</small>
                        </div>
                        <div class="col-6">
                          <div class="mb-2">
                            <i class="mdi mdi-clock-outline me-1"></i>
                            <strong>{{ downloadStatus.timeRemaining }}</strong>
                          </div>
                          <small class="text-muted">Time Remaining</small>
                        </div>
                      </div>

                      <div class="mt-3">
                        <small class="text-muted d-block">{{ downloadStatus.statusText }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Filters Section -->
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="btn-toolbar p-3 d-flex justify-content-between align-items-center w-100" role="toolbar">
                    <div class="flex-grow-1">
                      <div class="search-box mb-0 me-3">
                        <div class="position-relative">
                          <input v-model="search" type="text" class="form-control bg-light border-light rounded"
                                 placeholder="Search..." spellcheck="false">
                          <i class="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex">
                      <div class="d-flex align-items-center me-3 pe-3">
                        <span class="px-4 fw-semibold">Sub County:</span>
                        <select class="form-select" v-model="subCounty" @change="getSaccos()">
                          <option selected="" value="">All Sub Counties</option>
                          <option :key="index" :value="item" v-for="(item, index) in subCounties">
                            {{ item.subCountyName }}
                          </option>
                        </select>

                        <span class="px-4 fw-semibold">Ward:</span>
                        <select class="form-select" v-model="ward" @change="getSaccos()">
                          <option selected="" value="">All Wards</option>
                          <option :key="index" :value="item" v-for="(item, index) in wards">
                            {{ item.wardName }}
                          </option>
                        </select>

                        <span class="px-4 fw-semibold">Zone:</span>
                        <select class="form-select" v-model="zone" @change="getSaccos()">
                          <option selected="" value="">All Zones</option>
                          <option v-for="(item, index) in zones" :key="`${index}`" :value="item.zone">
                            {{item.zone}}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Data Table -->
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table align-middle table-striped table-nowrap table-hover dt-responsive contacts-table">
                      <thead class="table-dark">
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Sacco/Sub Group</th>
                        <th>Sub County</th>
                        <th scope="col">Ward</th>
                        <th scope="col">Zone</th>
                        <th scope="col">Uncompliant Vehicles</th>
                        <th scope="col">Compliant Vehicles</th>
                        <th>Total</th>
                        <th v-if="selectedMonth">Compliance %</th>
                        <th></th>
                      </tr>
                      </thead>
                      <tbody class="text-capitalize">
                      <tr :key="index" :value="item" v-for="(item, index) in saccos">
                        <th>{{index+1}}.</th>
                        <th>{{item.saccoName}}</th>
                        <td class="text-capitalize">{{item.subCountyName}}</td>
                        <td>{{item.wardName}}</td>
                        <td>{{item.zone}}</td>
                        <th>
                          <span class="text-danger">{{getUnCompliant(item.total,item.compliant)}}</span>
                        </th>
                        <th>
                          <span class="text-success">{{item.compliant}}</span>
                        </th>
                        <th>
                          <span class="text-black">{{item.total}}</span>
                        </th>
                        <th v-if="selectedMonth">
                                                    <span :class="getComplianceClass(item.total, item.compliant)">
                                                        {{ getCompliancePercentage(item.total, item.compliant) }}%
                                                    </span>
                        </th>
                        <td class="text-right">
                          <button v-if="getRight('PRINT STICKER')" @click="printSticker(item)"
                                  class="btn btn-warning btn-sm me-2">
                            <i class="mdi mdi-printer me-1"></i>Stickers
                          </button>

                          <button v-if="getRight('RECEIVE PAYMENT')" @click="receivePayment(item)"
                                  class="btn btn-success btn-sm me-2">
                            <i class="mdi mdi-cash-check me-1"></i>Receive Payment
                          </button>

                          <button @click="viewStatements(item)"
                                  class="btn btn-info btn-sm me-2">
                            <i class="mdi mdi-file-document me-1"></i>Statements
                          </button>

                          <button v-if="getRight('ADD VEHICLE TO SACCO')" @click="selectedSacco(item)"
                                  data-toggle="modal" data-target=".add-car-modal"
                                  class="btn btn-primary btn-sm me-2">
                            <i class="mdi mdi-plus-circle me-1"></i>Add Vehicle(s)
                          </button>

                          <a @click="goTo('parking-fleet-details',item.saccoName,item.saccoID,item.total,item.compliant,getUnCompliant(item.total,item.compliant))"
                             class="btn btn-secondary btn-sm">
                            <i class="mdi mdi-eye-outline me-1"></i>View
                          </a>
                        </td>
                      </tr>
                      </tbody>
                    </table>

                    <!-- Loading State -->
                    <div v-if="loading" class="card-body px-5 d-flex flex-column justify-items-center align-items-center text-center">
                      <div class="p-5 py-0 pt-3">
                        <div class="p-5">
                          <div class="spinner-border text-info m-1" role="status">
                            <span class="sr-only text-uppercase">Loading...</span>
                          </div>
                        </div>
                      </div>
                      <h4 class="text-uppercase">Loading Data</h4>
                    </div>

                    <!-- No Results State -->
                    <div v-if="saccos.length<1 && loading === false" class="card-body px-5 d-flex flex-column justify-items-center align-items-center text-center">
                      <div class="p-5 py-0 pt-3">
                        <img :src="'./assets/images/no-results.png'" class="img mb-4" alt="No search results"/>
                      </div>
                      <h4>No Results To Show</h4>
                      <p>Sorry, we could not find any results that match your search or filter criteria.</p>
                    </div>
                  </div>
                </div>

                <!-- Pagination -->
                <div class="card-footer bg-white">
                  <nav aria-label="Page navigation" class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                                            <span class="pr-3 pb-0 mb-0 font-13px">
                                                <span>Rows Per Page</span>
                                            </span>
                      <select class="form-select w-auto font-13px" v-model="pageSize">
                        <option value="10">10 Rows</option>
                        <option value="25">25 Rows</option>
                        <option value="50">50 Rows</option>
                        <option value="100">100 Rows</option>
                        <option value="150">150 Rows</option>
                        <option value="200">200 Rows</option>
                      </select>
                    </div>

                    <ul class="pagination">
                      <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" @click="prevPage" :disabled="currentPage === 1">Previous</a>
                      </li>
                      <li v-for="page in pagination(currentPage, totalPages, 3)" :key="page"
                          class="page-item" :class="{ active: currentPage === page }">
                        <span v-if="page === '...'" class="page-link">{{ page }}</span>
                        <a v-else class="page-link" @click="goToPage(page)">{{ page }}</a>
                      </li>
                      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" @click="nextPage" :disabled="currentPage === totalPages">Next</a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Add Vehicle Modal -->
      <div class="modal fade add-car-modal" tabindex="-1" role="dialog" aria-labelledby="myLargeModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-xl modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="myLargeModalLabel">{{sacco.saccoName}}</h5>
              <button type="button" class="btn-close" data-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div>
                <div class="col-12">
                  <address>
                    <strong>About Sacco:</strong><br>
                    Contact Person: {{sacco.fullNames}}<br>
                    {{sacco.phoneNumber}}<br>
                    {{sacco.physicalAddress}}
                    <br><br>
                    {{sacco.wardName}}, {{sacco.subCountyName}}
                  </address>
                </div>
                <div class="new-vehicles-container-modal">
                  <div class="col-12">
                    <div class="py-2 mt-3">
                      <h3 class="font-size-15 fw-bold">Vehicles to Be Newly Registered</h3>
                    </div>
                    <div class="py-1 mt-3">
                      <select @change="selectedIncomeType($event)" title="-- Income Type --" class="form-control show-tick" data-live-search="true" v-model="incomeType">
                        <option v-for="(item, index) in incomeTypes" :value="JSON.stringify(item)" :key="index">
                          {{ item.incomeTypeDescription }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="">
                      <table class="table table-editable-1 align-middle table-edits">
                        <thead class="table-light">
                        <tr class="text-uppercase table-dark">
                          <th>#</th>
                          <th>Vehicle</th>
                          <th class="">Quantity</th>
                          <th class="">Category</th>
                          <th class="">Owner</th>
                          <th>Phone No.</th>
                          <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item,index) in vehicles" :key="index">
                          <td style="width: 80px" class="categoryIndex">{{index+1}}</td>

                          <td class="">
                            <input v-model="item.numberPlate" type="text" class="form-control" placeholder="Enter Plate No." spellcheck="false">
                          </td>

                          <td class="">
                            <input v-model="item.quantity" type="text" class="form-control" placeholder="Quantity(n)" spellcheck="false">
                          </td>

                          <td>
                            <select v-model="item.category" class="form-control bg-white" title="Select Traffic offence" data-live-search="true">
                              <option v-for="(item2, index) in feesAndCharges" :value="item2.feeId+':'+ item2.feeDescription+':'+ item2.unitFeeAmount" :key="index">
                                {{ item2.feeDescription }} KES {{totalAmount(item2.unitFeeAmount,item.quantity)}}
                              </option>
                            </select>
                          </td>

                          <td class="">
                            <input v-model="item.owner" type="text" class="form-control" placeholder="Owner's Name" spellcheck="false">
                          </td>

                          <td class="">
                            <input v-model="item.phoneNumber" type="text" class="form-control" placeholder="Owner's Phone No." spellcheck="false">
                          </td>

                          <td @click="removeVehicle(index)" class="text-right cell-change d-flex align-items-center justify-content-end">
                            <a class="btn btn-light btn-rounded waves-effect btn-circle btn-transparent cancel-new-category-2" title="Cancel"><i class="bx bx-x"></i></a>
                          </td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                          <td @click="addVehicle()" colspan="7" class="bg-light add-field-1 cursor-pointer">
                            <span class="d-flex align-items-center"><i class="dripicons-plus mr-5 d-flex justify-content-center align-items-center font-21"></i><span class="pl-5">Add A Vehicle</span></span>
                          </td>
                        </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                <div class="d-none loader-panel">
                  <div class="p-5 py-0 pt-3 d-flex justify-content-center align-items-center text-center flex-column">
                    <div class="p-5">
                      <div class="spinner-border text-info m-1" role="status">
                        <span class="sr-only text-uppercase">Loading...</span>
                      </div>
                    </div>
                    <h4 class="text-black fw-bold">Processing...</h4>
                    <div class="mb-5">Please wait while we register the vehicles</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <div class="float-end">
                <a @click="registerVehicles()" href="javascript: void(0);" data-dismiss="modal" class="btn btn-primary w-md waves-effect waves-light reg-payment">Submit</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End Add Vehicle Modal -->

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">©County</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">County Government</div>
            </div>
          </div>
        </div>
      </footer>
    </div>

  </div>

  </body>

</template>

<script>

/* eslint-disable */

import {parking, execute, biller, getRight, authUrl} from "@/api";
import NavigationBar from "@/components/Navigation";
import ToolBar from "@/components/parking/ToolBar";
import router from "@/router";

export default {
  name: "ParkingFleet",
  components: {ToolBar, NavigationBar},
  data() {
    return {
      summary: {count: 0},
      loading: false,
      currentPage: 1,
      totalPages: 0,
      pageSize: 10,
      search: '',
      uncompliantVehicles: 0,
      compliantVehicles: 0,
      registeredVehicles: 0,

      // Month/Year Selection
      selectedMonth: (new Date().getMonth() + 1).toString().padStart(2, '0'), // Current month as default
      selectedYear: new Date().getFullYear(),
      availableMonths: [
        {value: '01', label: 'January'},
        {value: '02', label: 'February'},
        {value: '03', label: 'March'},
        {value: '04', label: 'April'},
        {value: '05', label: 'May'},
        {value: '06', label: 'June'},
        {value: '07', label: 'July'},
        {value: '08', label: 'August'},
        {value: '09', label: 'September'},
        {value: '10', label: 'October'},
        {value: '11', label: 'November'},
        {value: '12', label: 'December'}
      ],
      availableYears: [],

      // Download Status
      downloadStatus: {
        downloading: false,
        currentBatch: 0,
        totalBatches: 0,
        percentage: 0,
        recordsProcessed: 0,
        timeRemaining: '0s',
        message: '',
        statusText: '',
        startTime: null
      },

      // AI Insights
      aiInsights: {
        generated: false,
        bestPerformers: [],
        worstPerformers: [],
        mostImproved: [],
        keyMetrics: [],
        recommendations: []
      },

      // Previous month data for comparison
      previousMonthData: [],

      saccos: [],
      sacco: {},
      vehicles: [{numberPlate: null, category: null, owner: null, phoneNumber: null, quantity: 1}],
      subCounties: [{subCountyID: "", subCountyName: ""}],
      wards: [{wardID: "", wardName: ""}],
      subCounty: {subCountyID: null, subCountyName: null},
      ward: {wardID: null, wardName: null},
      zones: [],
      zone: '',
      incomeType: {
        incomeTypeId: null,
        incomeTypeDescription: null,
        incomeTypePrefix: null,
        fundDescription: null,
        status: null
      },
      feesAndCharges: [{
        feeId: null,
        feeDescription: null,
        unitOfMeasure: null,
        unitFeeAmount: null,
        accountNo: null,
        incomeTypeId: null,
        feeType: null,
        accountDesc: null,
        costCenterNo: null,
        zone: null,
        typeDescription: null
      }],
      incomeTypes: [{
        incomeTypeId: null,
        incomeTypeDescription: null,
        incomeTypePrefix: null,
        fundDescription: null,
        status: null
      }]
    }
  },

  computed: {
    allSaccosData() {
      // This will hold all downloaded data
      return this.saccos;
    }
  },

  mounted() {
    // Generate years from 2020 to current year + 1
    const currentYear = new Date().getFullYear();
    for (let year = 2020; year <= currentYear + 1; year++) {
      this.availableYears.push(year);
    }

    this.getSaccos();
    this.getIncomeTypes();
    this.getSubCounties();
    this.menuAccess();

    this.$watch('search', function () {
      this.getSaccos();
    });

    this.$watch('pageSize', function (newPageSize) {
      const totalItems = this.summary.count;
      const newTotalPages = Math.ceil(totalItems / newPageSize);
      this.totalPages = newTotalPages;
      this.currentPage = 1;
      this.getSaccos();
    });

    this.$watch('subCounty', function () {
      this.subCountyID = this.subCounty.subCountyID;
      this.subCountyName = this.subCounty.subCountyName;
      this.getWards();
    });

    this.$watch('ward', function () {
      this.wardID = this.ward.wardID;
      this.wardName = this.ward.wardName;
      this.getZones(this.wardID);
    });
  },

  methods: {

    // ============================================
    // AI INSIGHTS METHODS
    // ============================================

    async generateInsights() {
      if (this.saccos.length === 0) {
        alert('No data available to generate insights');
        return;
      }

      // Calculate compliance rates for all SACCOs
      const saccosWithRates = this.saccos.map(sacco => ({
        ...sacco,
        complianceRate: this.getCompliancePercentage(sacco.total, sacco.compliant),
        nonCompliant: this.getUnCompliant(sacco.total, sacco.compliant)
      })).filter(sacco => parseInt(sacco.total) > 0);

      // Sort by compliance rate
      const sortedByCompliance = [...saccosWithRates].sort((a, b) => b.complianceRate - a.complianceRate);

      // Best Performers (top 3)
      this.aiInsights.bestPerformers = sortedByCompliance.slice(0, 3);

      // Worst Performers (bottom 3)
      this.aiInsights.worstPerformers = sortedByCompliance.slice(-3).reverse();

      // Most Improved (requires previous month data)
      await this.calculateMostImproved();

      // Generate Key Metrics
      this.generateKeyMetrics(saccosWithRates);

      // Generate Recommendations
      this.generateRecommendations(saccosWithRates);

      this.aiInsights.generated = true;
    },

    async calculateMostImproved() {
      if (!this.selectedMonth || !this.selectedYear) {
        this.aiInsights.mostImproved = [];
        return;
      }

      // Calculate previous month
      let prevMonth = parseInt(this.selectedMonth) - 1;
      let prevYear = this.selectedYear;

      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear = prevYear - 1;
      }

      // Fetch previous month data
      try {
        const data = new FormData();
        data.append("function", "getSaccos");
        data.append("page", 1);
        data.append("rows_per_page", 10000);
        data.append("selectedMonth", prevMonth.toString().padStart(2, '0'));
        data.append("selectedYear", prevYear);
        data.append("keyword", '');

        const response = await execute(data, parking);

        if (response.data.success) {
          const previousData = response.data.data.saccos;

          // Compare current and previous
          const improvements = [];

          this.saccos.forEach(currentSacco => {
            const prevSacco = previousData.find(p => p.saccoID === currentSacco.saccoID);

            if (prevSacco && parseInt(prevSacco.total) > 0 && parseInt(currentSacco.total) > 0) {
              const currentRate = this.getCompliancePercentage(currentSacco.total, currentSacco.compliant);
              const previousRate = this.getCompliancePercentage(prevSacco.total, prevSacco.compliant);
              const improvement = currentRate - previousRate;

              if (improvement > 0) {
                improvements.push({
                  ...currentSacco,
                  currentRate,
                  previousRate,
                  improvement: Math.round(improvement)
                });
              }
            }
          });

          // Sort by improvement and take top 3
          improvements.sort((a, b) => b.improvement - a.improvement);
          this.aiInsights.mostImproved = improvements.slice(0, 3);
        }
      } catch (error) {
        console.error('Error fetching previous month data:', error);
        this.aiInsights.mostImproved = [];
      }
    },

    generateKeyMetrics(saccosWithRates) {
      const metrics = [];

      // Overall compliance rate
      const overallRate = this.compliantVehicles > 0 && this.registeredVehicles > 0
          ? Math.round((this.compliantVehicles / this.registeredVehicles) * 100)
          : 0;
      metrics.push(`Overall Compliance Rate: ${overallRate}%`);

      // Average SACCO size
      const avgVehicles = this.registeredVehicles / this.saccos.length;
      metrics.push(`Average SACCO Size: ${Math.round(avgVehicles)} vehicles`);

      // High performers count
      const highPerformers = saccosWithRates.filter(s => s.complianceRate >= 80).length;
      metrics.push(`${highPerformers} SACCOs with 80%+ compliance`);

      // Low performers count
      const lowPerformers = saccosWithRates.filter(s => s.complianceRate < 50).length;
      metrics.push(`${lowPerformers} SACCOs need immediate attention`);

      // Revenue potential
      const avgFee = 2000; // Assuming average fee
      const potentialRevenue = this.uncompliantVehicles * avgFee;
      metrics.push(`Potential Revenue: KES ${this.numberFormat(potentialRevenue)}`);

      this.aiInsights.keyMetrics = metrics;
    },

    generateRecommendations(saccosWithRates) {
      const recommendations = [];

      // Based on worst performers
      if (this.aiInsights.worstPerformers.length > 0) {
        const worstNames = this.aiInsights.worstPerformers.map(s => s.saccoName).join(', ');
        recommendations.push(`Focus enforcement efforts on ${worstNames}`);
      }

      // Based on overall compliance
      const overallRate = Math.round((this.compliantVehicles / this.registeredVehicles) * 100);
      if (overallRate < 60) {
        recommendations.push('Launch awareness campaign - compliance is below 60%');
      } else if (overallRate > 80) {
        recommendations.push('Maintain current strategy - compliance is excellent');
      }

      // Based on SACCO distribution
      const smallSaccos = saccosWithRates.filter(s => parseInt(s.total) < 20).length;
      if (smallSaccos > this.saccos.length / 2) {
        recommendations.push('Consider consolidating smaller SACCOs for better management');
      }

      // Based on improvements
      if (this.aiInsights.mostImproved.length > 0) {
        const topImproved = this.aiInsights.mostImproved[0];
        recommendations.push(`Study ${topImproved.saccoName}'s success strategy for replication`);
      }

      // Time-based recommendations
      if (this.selectedMonth) {
        const month = parseInt(this.selectedMonth);
        if (month === 12 || month === 1) {
          recommendations.push('Send end-of-year renewal reminders to all SACCOs');
        }
      }

      this.aiInsights.recommendations = recommendations;
    },

    // ============================================
    // CLIENT-SIDE CSV GENERATION METHODS
    // ============================================

    downloadCurrentPage() {
      if (this.saccos.length === 0) {
        alert('No data to download');
        return;
      }

      const csvContent = this.generateCSV(this.saccos, false);
      const periodLabel = this.selectedMonth ?
          `_${this.getSelectedPeriodLabel().replace(/ /g, '_')}` :
          '_Current_Status';
      this.downloadCSVFile(csvContent, `Parking_Fleet_Page_${this.currentPage}${periodLabel}.csv`);
    },

    async downloadAllData() {
      // Calculate total batches needed
      const totalRecords = this.summary.count;
      const batchSize = 100; // Fetch 100 records per batch
      const totalBatches = Math.ceil(totalRecords / batchSize);

      // Initialize download status
      this.downloadStatus = {
        downloading: true,
        currentBatch: 0,
        totalBatches: totalBatches,
        percentage: 0,
        recordsProcessed: 0,
        timeRemaining: 'Calculating...',
        message: 'Initializing...',
        statusText: 'Preparing to download data...',
        startTime: Date.now()
      };

      const allData = [];

      try {
        // Fetch data in batches
        for (let batch = 1; batch <= totalBatches; batch++) {
          this.downloadStatus.statusText = `Fetching batch ${batch} of ${totalBatches}...`;
          this.downloadStatus.currentBatch = batch;

          const data = new FormData();
          data.append("function", "getSaccos");
          data.append("page", batch);
          data.append("rows_per_page", batchSize);
          data.append("keyword", this.search || '');
          data.append("selectedMonth", this.selectedMonth);
          data.append("selectedYear", this.selectedYear);
          data.append("subCountyName", this.subCounty.subCountyName || '');
          data.append("wardName", this.ward.wardName || '');
          data.append("zone", this.zone || '');

          const response = await execute(data, parking);

          if (response.data.success) {
            const batchData = response.data.data.saccos;
            allData.push(...batchData);

            // Update progress
            this.downloadStatus.recordsProcessed = allData.length;
            this.downloadStatus.percentage = Math.round((batch / totalBatches) * 100);

            // Calculate time remaining
            const elapsed = Date.now() - this.downloadStatus.startTime;
            const avgTimePerBatch = elapsed / batch;
            const remainingBatches = totalBatches - batch;
            const remainingTime = Math.round((avgTimePerBatch * remainingBatches) / 1000);
            this.downloadStatus.timeRemaining = this.formatTime(remainingTime);

            // Small delay to prevent overwhelming the server
            await this.delay(200);
          } else {
            throw new Error(response.data.message || 'Failed to fetch data');
          }
        }

        // Generate and download CSV
        this.downloadStatus.statusText = 'Generating CSV file...';
        const csvContent = this.generateCSV(allData, true);

        const periodLabel = this.selectedMonth ?
            `_${this.getSelectedPeriodLabel().replace(/ /g, '_')}` :
            '_Current_Status';
        this.downloadCSVFile(csvContent, `Parking_Fleet_Complete${periodLabel}.csv`);

        this.downloadStatus.statusText = 'Download complete!';

        // Close progress after 2 seconds
        setTimeout(() => {
          this.downloadStatus.downloading = false;
        }, 2000);

      } catch (error) {
        console.error('Download error:', error);
        alert('Error downloading data: ' + error.message);
        this.downloadStatus.downloading = false;
      }
    },

    async downloadWithInsights() {
      // First generate insights if not already generated
      if (!this.aiInsights.generated) {
        await this.generateInsights();
      }

      // Download all data first
      await this.downloadAllData();
    },

    generateCSV(data, includeInsights = false) {
      let csv = [];

      // Header Section
      csv.push(['PARKING FLEET COMPLIANCE REPORT']);
      csv.push(['County Government of Kenya']);
      csv.push([]);

      // Report Details
      const periodLabel = this.selectedMonth ?
          this.getSelectedPeriodLabel() :
          'All Time (Current Status)';
      csv.push(['Report Period:', periodLabel]);
      csv.push(['Generated On:', new Date().toLocaleString('en-KE')]);
      csv.push(['Generated By:', sessionStorage.getItem('sessionNames') || 'System']);
      csv.push([]);

      // Applied Filters
      if (this.search || this.subCounty.subCountyName || this.ward.wardName || this.zone) {
        csv.push(['APPLIED FILTERS']);
        if (this.search) csv.push(['Search:', this.search]);
        if (this.subCounty.subCountyName) csv.push(['Sub County:', this.subCounty.subCountyName]);
        if (this.ward.wardName) csv.push(['Ward:', this.ward.wardName]);
        if (this.zone) csv.push(['Zone:', this.zone]);
        csv.push([]);
      }

      // AI Insights Section (if requested and available)
      if (includeInsights && this.aiInsights.generated) {
        csv.push(['AI-POWERED INSIGHTS']);
        csv.push([]);

        // Best Performers
        csv.push(['TOP PERFORMERS']);
        csv.push(['Rank', 'SACCO Name', 'Compliance Rate', 'Compliant', 'Total']);
        this.aiInsights.bestPerformers.forEach((sacco, index) => {
          csv.push([
            index + 1,
            sacco.saccoName,
            sacco.complianceRate + '%',
            sacco.compliant,
            sacco.total
          ]);
        });
        csv.push([]);

        // Needs Attention
        csv.push(['NEEDS ATTENTION']);
        csv.push(['Rank', 'SACCO Name', 'Compliance Rate', 'Compliant', 'Total']);
        this.aiInsights.worstPerformers.forEach((sacco, index) => {
          csv.push([
            index + 1,
            sacco.saccoName,
            sacco.complianceRate + '%',
            sacco.compliant,
            sacco.total
          ]);
        });
        csv.push([]);

        // Most Improved
        if (this.aiInsights.mostImproved.length > 0) {
          csv.push(['MOST IMPROVED']);
          csv.push(['Rank', 'SACCO Name', 'Previous Rate', 'Current Rate', 'Improvement']);
          this.aiInsights.mostImproved.forEach((sacco, index) => {
            csv.push([
              index + 1,
              sacco.saccoName,
              sacco.previousRate + '%',
              sacco.currentRate + '%',
              '+' + sacco.improvement + '%'
            ]);
          });
          csv.push([]);
        }

        // Key Metrics
        csv.push(['KEY METRICS']);
        this.aiInsights.keyMetrics.forEach(metric => {
          csv.push([metric]);
        });
        csv.push([]);

        // Recommendations
        csv.push(['RECOMMENDATIONS']);
        this.aiInsights.recommendations.forEach(rec => {
          csv.push([rec]);
        });
        csv.push([]);
        csv.push([]);
      }

      // Main Data Table
      csv.push(['DETAILED SACCO DATA']);
      csv.push([]);

      // Headers
      const headers = [
        'No.',
        'SACCO/Sub Group',
        'Sub County',
        'Ward',
        'Zone',
        'Total Vehicles',
        'Compliant Vehicles',
        'Non-Compliant Vehicles',
        'Compliance %',
        'Contact Person',
        'Phone Number',
        'Physical Address'
      ];
      csv.push(headers);

      // Data rows
      let totalVehicles = 0;
      let totalCompliant = 0;

      data.forEach((item, index) => {
        const total = parseInt(item.total) || 0;
        const compliant = parseInt(item.compliant) || 0;
        const nonCompliant = total - compliant;
        const compliancePercent = this.getCompliancePercentage(total, compliant);

        totalVehicles += total;
        totalCompliant += compliant;

        csv.push([
          index + 1,
          item.saccoName,
          item.subCountyName,
          item.wardName,
          item.zone || '-',
          total,
          compliant,
          nonCompliant,
          compliancePercent + '%',
          item.fullNames || '-',
          item.phoneNumber || '-',
          item.physicalAddress || '-'
        ]);
      });

      // Summary Section
      const totalNonCompliant = totalVehicles - totalCompliant;
      const overallCompliance = totalVehicles > 0 ?
          Math.round((totalCompliant / totalVehicles) * 100) : 0;

      csv.push([]);
      csv.push([]);
      csv.push(['SUMMARY STATISTICS']);
      csv.push(['Total SACCOs:', data.length]);
      csv.push(['Total Vehicles:', totalVehicles]);
      csv.push(['Total Compliant:', totalCompliant, `(${Math.round((totalCompliant/totalVehicles)*100)}%)`]);
      csv.push(['Total Non-Compliant:', totalNonCompliant, `(${Math.round((totalNonCompliant/totalVehicles)*100)}%)`]);
      csv.push(['Overall Compliance Rate:', overallCompliance + '%']);
      csv.push([]);

      // Compliance Ranges
      const excellent = data.filter(s => this.getCompliancePercentage(s.total, s.compliant) >= 80).length;
      const good = data.filter(s => {
        const rate = this.getCompliancePercentage(s.total, s.compliant);
        return rate >= 60 && rate < 80;
      }).length;
      const fair = data.filter(s => {
        const rate = this.getCompliancePercentage(s.total, s.compliant);
        return rate >= 40 && rate < 60;
      }).length;
      const poor = data.filter(s => this.getCompliancePercentage(s.total, s.compliant) < 40).length;

      csv.push(['COMPLIANCE DISTRIBUTION']);
      csv.push(['Excellent (80-100%):', excellent, 'SACCOs']);
      csv.push(['Good (60-79%):', good, 'SACCOs']);
      csv.push(['Fair (40-59%):', fair, 'SACCOs']);
      csv.push(['Poor (0-39%):', poor, 'SACCOs']);
      csv.push([]);

      // Footer
      csv.push([]);
      csv.push(['Report Generated:', new Date().toLocaleString('en-KE')]);
      csv.push(['System:', 'County Parking Management System']);

      // Convert to CSV string
      return csv.map(row =>
          row.map(cell => {
            // Escape cells that contain commas, quotes, or newlines
            if (cell === null || cell === undefined) return '';
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
      ).join('\n');
    },

    downloadCSVFile(csvContent, filename) {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },

    // ============================================
    // UTILITY METHODS
    // ============================================

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    formatTime(seconds) {
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    },

    getSelectedPeriodLabel() {
      if (!this.selectedMonth) return 'All Time';
      const monthLabel = this.availableMonths.find(m => m.value === this.selectedMonth)?.label;
      return `${monthLabel} ${this.selectedYear}`;
    },

    resetFilters() {
      this.selectedMonth = '';
      this.selectedYear = new Date().getFullYear();
      this.subCounty = '';
      this.ward = '';
      this.zone = '';
      this.search = '';
      this.getSaccos();
    },

    getCompliancePercentage(total, compliant) {
      if (total === 0 || total === '0') return 0;
      return Math.round((compliant / total) * 100);
    },

    getComplianceClass(total, compliant) {
      const percentage = this.getCompliancePercentage(total, compliant);
      if (percentage >= 80) return 'badge bg-success';
      if (percentage >= 50) return 'badge bg-warning';
      return 'badge bg-danger';
    },

    getUnCompliant(total, compliant) {
      return total - compliant;
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num);
    },

    // ============================================
    // API METHODS (Keep existing ones)
    // ============================================

    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Parking Fleet");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller).then(() => {}).catch(() => {});
    },

    getSaccos() {
      const search = this.search === undefined ? '' : this.search;
      this.saccos.splice(0);
      this.loading = true;
      const data = new FormData();
      data.append("function", "getSaccos");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("keyword", search);
      data.append("selectedMonth", this.selectedMonth);
      data.append("selectedYear", this.selectedYear);
      data.append("subCountyName", this.subCounty.subCountyName || '');
      data.append("wardName", this.ward.wardName || '');
      data.append("zone", this.zone || '');

      execute(data, parking)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.saccos = res.data.data.saccos;

              this.registeredVehicles = this.saccos.reduce((acc, cur) => {
                let totalAmount = Number(cur.total);
                return acc + totalAmount;
              }, 0);

              this.compliantVehicles = this.saccos.reduce((acc, cur) => {
                let totalAmount = Number(cur.compliant);
                return acc + totalAmount;
              }, 0);

              this.uncompliantVehicles = this.registeredVehicles - this.compliantVehicles;

              this.summary = res.data.data.summary;
              this.totalPages = Math.ceil(this.summary.count / this.pageSize);

              // Auto-generate insights after data loads
              if (this.saccos.length > 0) {
                this.generateInsights();
              }
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounty");
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    getWards() {
      this.wards = [];
      this.ward = '';
      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", this.subCounty.subCountyID);
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    getZones(wardID) {
      this.zones.splice(0);
      const data = new FormData();
      data.append("function", "getZones");
      data.append("wardID", wardID);
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.zones = res.data.data.zones;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    getIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "PKN");
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.incomeTypes = res.data.data.incomeTypes;
            }
          })
          .catch((e) => {
            console.error(e.message);
          });
    },

    getRight(type) {
      return getRight(type);
    },

    selectedSacco(item) {
      this.sacco = item;
    },

    printSticker(item) {
      this.sacco = item;
      localStorage['params'] = JSON.stringify({
        saccoID: this.sacco.saccoID
      });

      const data = new FormData();
      data.append("function", "getStickers");
      data.append("saccoID", this.sacco.saccoID);
      execute(data, parking)
          .then((res) => {
            if (res.data.success) {
              const routeData = this.$router.resolve({name: 'parking-sticker'});
              window.open(routeData.href, '_blank');
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            alert(e.message);
          });
    },

    goTo(route, saccoName, saccoID, total, compliant, unCompliant) {
      sessionStorage.setItem("saccoName", saccoName);
      sessionStorage.setItem("saccoID", saccoID);
      sessionStorage.setItem("total", total);
      sessionStorage.setItem("compliant", compliant);
      sessionStorage.setItem("unCompliant", unCompliant);
      router.push(route);
    },

    // Pagination methods
    pagination(currentPage, totalPages, maxVisibleButtons) {
      let pages = [];
      let start = currentPage - Math.floor(maxVisibleButtons / 2);
      let end = currentPage + Math.floor(maxVisibleButtons / 2);

      if (start < 1) {
        start = 1;
        end = start + maxVisibleButtons - 1;
        if (end > totalPages) {
          end = totalPages;
        }
      }
      if (end > totalPages) {
        end = totalPages;
        start = end - maxVisibleButtons + 1;
        if (start < 1) {
          start = 1;
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (start > 1) {
        pages.unshift("...");
      }
      if (end < totalPages) {
        pages.push("...");
      }

      return pages;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getSaccos();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getSaccos();
      }
    },

    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getSaccos();
      }
    },

    // Payment and Statements Methods
    receivePayment(item) {
      // Store SACCO details for payment processing (matching original code)
      sessionStorage.setItem("saccoName", item.saccoName);
      sessionStorage.setItem("saccoID", item.saccoID);
      sessionStorage.setItem("total", item.total);
      sessionStorage.setItem("compliant", item.compliant);
      sessionStorage.setItem("unCompliant", this.getUnCompliant(item.total, item.compliant));

      // Navigate to payment page using the correct route from original code
      router.push('parking-fleet-payment');
    },

    viewStatements(item) {
      // Navigate to statements page for this SACCO (matching original code)
      sessionStorage.setItem("saccoName", item.saccoName);
      sessionStorage.setItem("saccoID", item.saccoID);
      sessionStorage.setItem("total", item.total);
      sessionStorage.setItem("compliant", item.compliant);
      sessionStorage.setItem("unCompliant", this.getUnCompliant(item.total, item.compliant));

      // Navigate to statements page using the correct route from original code
      router.push('parking-fleet-details-history');
    },

    // Add Vehicle Methods
    selectedIncomeType(event) {
      const value = event.target.value;
      const result = JSON.parse(value);
      this.getFeesAndChargesBilling(result.incomeTypeId);
    },

    getFeesAndChargesBilling(incomeTypeId) {
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.feesAndCharges = res.data.data.feesAndCharges;
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    totalAmount(amount, months) {
      // Convert amount and months to numbers before multiplying
      amount = Number(amount);
      months = Number(months);
      // Return the result of multiplication
      return amount * months;
    },

    addVehicle(numberPlate, category, owner, phoneNumber) {
      this.vehicles.push({
        numberPlate: numberPlate,
        category: category,
        owner: owner,
        phoneNumber: phoneNumber,
        quantity: 1
      });
    },

    removeVehicle(position) {
      this.vehicles.splice(position, 1);
    },

    registerVehicles() {
      const data = new FormData();
      data.append("function", "registerVehicles");
      data.append("sacco", JSON.stringify(this.sacco));
      data.append("vehicles", JSON.stringify(this.vehicles));
      execute(data, parking)
          .then((res) => {
            if (res.data.success) {
              alert('Vehicles registered successfully!');
              this.getSaccos();
              // Reset vehicles array
              this.vehicles = [{numberPlate: null, category: null, owner: null, phoneNumber: null, quantity: 1}];
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            alert('Error: ' + e.message);
          });
    },

    selectedSacco(item) {
      console.log("###", item);
      this.sacco = item;
    }
  }
}
</script>

<style scoped>
.insight-box {
  border: 2px solid;
  border-radius: 10px;
  padding: 20px;
  height: 100%;
}

.insight-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.progress {
  background-color: #f0f0f0;
}

.progress-bar {
  background: linear-gradient(90deg, #007bff, #0056b3);
  transition: width 0.3s ease;
}

.card-header.bg-success {
  background: linear-gradient(135deg, #28a745, #20c997) !important;
}

.insight-box.border-success {
  border-color: #28a745 !important;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05), rgba(32, 201, 151, 0.05));
}

.insight-box.border-danger {
  border-color: #dc3545 !important;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.05), rgba(253, 126, 20, 0.05));
}

.insight-box.border-warning {
  border-color: #ffc107 !important;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.05), rgba(255, 152, 0, 0.05));
}
</style>